import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Check, Lightbulb } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DailyLessonCard = () => {
  const { access } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/daily-lesson");
      setData(r.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (access && !access.has_access) { setLoading(false); return; }
    load();
  }, [access]);

  const complete = async () => {
    setCompleting(true);
    try {
      const r = await api.post("/daily-lesson/complete");
      setData((d) => ({ ...d, completed_today: true, current_streak: r.data.current_streak, longest_streak: r.data.longest_streak }));
      if (!r.data.already) toast.success(`Streak: ${r.data.current_streak} day${r.data.current_streak>1?"s":""}`);
    } catch { toast.error("Could not update streak"); }
    setCompleting(false);
  };

  if (loading) return <div className="card-surface p-8 min-h-[200px] animate-pulse" data-testid="daily-loading"/>;
  if (!data) return null;

  return (
    <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} className="card-surface p-8 relative overflow-hidden" data-testid="daily-lesson-card">
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-amber-500 flex items-center gap-2"><Lightbulb className="w-3.5 h-3.5"/> TODAY'S LESSON</div>
        <div className="flex items-center gap-2 text-sm" data-testid="streak-count">
          <Flame className={`w-4 h-4 ${data.current_streak > 0 ? "text-amber-500" : "text-neutral-600"}`} />
          <span className={data.current_streak > 0 ? "text-amber-400 font-semibold" : "text-neutral-500"}>
            {data.current_streak} day{data.current_streak===1?"":"s"}
          </span>
        </div>
      </div>
      <div className="overline text-neutral-500 mb-2">{data.lesson.category}</div>
      <h3 className="font-serif text-2xl md:text-3xl mb-3 leading-tight">{data.lesson.title}</h3>
      <p className="text-neutral-300 leading-relaxed mb-6" data-testid="daily-lesson-body">{data.lesson.body}</p>
      <div className="flex flex-wrap gap-3 items-center">
        <button
          onClick={complete}
          disabled={completing || data.completed_today}
          data-testid="daily-complete-btn"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border transition-colors ${
            data.completed_today
              ? "bg-amber-500/10 border-amber-500/40 text-amber-400"
              : "btn-amber"
          }`}
        >
          {data.completed_today ? <><Check className="w-4 h-4"/> Done for today</> : <>Mark as done</>}
        </button>
        {data.longest_streak > 0 && (
          <span className="text-xs text-neutral-500">Longest streak: {data.longest_streak} day{data.longest_streak===1?"":"s"}</span>
        )}
      </div>
    </motion.div>
  );
};

export default DailyLessonCard;
